import { createApi } from '@reduxjs/toolkit/dist/query/react'
import { mercuryBaseQuery } from 'api/base'
import globalCookies from '../helpers/cookies'

import { accountAPI } from 'api/account'
import { domainAPI } from 'api/domain'
import { supportAPI } from 'api/support'
import { shopAPI } from 'api/shop'
import { billingAPI } from 'api/billing'

import { Session, SignUpFields, InitialiseApplication } from 'models/app'
import { Login, ResetPassword, TwoFactorMethodList } from 'models/authentication'
import { CurrentAccount, SecurityQuestionList } from 'models/account'

/**********************************************************************************************************
 *   APP API
 **********************************************************************************************************/
export const appAPI = createApi({
    reducerPath: 'appAPI',
    baseQuery: mercuryBaseQuery,
    tagTypes: ['InitialiseApplication', 'User'],
    endpoints: (builder) => ({
        getToken: builder.query<null, void>({
            query: () => 'client/csrf-token'
        }),
        initialiseApplication: builder.query<InitialiseApplication, void>({
            async queryFn(_arg, _queryApi, _extraOptions, baseQuery) {
                await baseQuery('client/csrf-token')

                /* const existingSettings = localStorage.getItem('mcaTheme') */
                const settings: any = /* existingSettings ? JSON.parse(existingSettings) : */ await baseQuery('client/settings')
                const session: any = await baseQuery('client/session')

                /* if (!existingSettings) localStorage.setItem('mcaTheme', JSON.stringify(settings)) */

                if (settings.error) {
                    return { error: settings.error }
                }

                if (session.error) {
                    return { error: session.error }
                }

                return {
                    data: {
                        session: session.data.data,
                        settings: settings.data.data
                    }
                }
            },
            providesTags: ['InitialiseApplication']
        }),
        login: builder.mutation<any, Login>({
            async queryFn(_arg, _queryApi, _extraOptions, baseQuery) {
                const login: any = await baseQuery({
                    url: 'client/login',
                    method: 'POST',
                    body: _arg
                })
                if (login.error) return { error: login.error }

                const session: any = await baseQuery('client/session')
                if (session.error) return { error: session.error }

                const { two_factor_required, current_account } = session.data.data

                if (two_factor_required) { // Redirect to 2fa if user has authed their email and password but require 2fa
                    /** ************************************ TWO FACTOR **************************************/
                    const two_factor: any = await baseQuery('client/auth/two-factor')

                    return {
                        data: {
                            session: session.data?.data ? session.data?.data : { error: { status: session.error?.status, data: session.error?.data?.messages[0] } },
                            two_factor: two_factor.data?.data ? two_factor.data?.data : { error: { status: two_factor.error?.status, data: two_factor.error?.data?.messages[0] } }
                        }
                    }
                }

                const authorised_accounts: any = await baseQuery('client/selected-account')

                /** ************************************ SINGLE ACCOUNT LOGIN **************************************/
                if (!current_account && authorised_accounts?.data?.data.length === 1) {
                    const default_account: CurrentAccount = authorised_accounts.data.data[0]

                    /** ************************************ SELECT AUTHORISED ACCOUNT **************************************/
                    await baseQuery({
                        url: `client/selected-account/${default_account.account.id}`,
                        method: 'POST',
                        body: default_account.account.id
                    })

                    /** ************************************ FETCH USER DATA **************************************/
                    const user: any = await baseQuery('client/me')
                    if (user.error) return { error: user.error }

                    return {
                        data: {
                            session: {
                                authentication: true,
                                two_factor_required: false,
                                account: {
                                    ...default_account.account,
                                    is_primary: default_account.is_primary,
                                    expiry: default_account.expiry,
                                    role: default_account.role
                                },
                                user: user.data.data
                            }
                        }
                    }
                }

                return {
                    data: {
                        login: login.data?.data ? login.data?.data : { error: { status: login.error?.status, data: login.error?.data?.messages[0] } },
                        session: session.data?.data ? session.data?.data : { error: { status: session.error?.status, data: session.error?.data?.messages[0] } },
                        authorised_accounts: authorised_accounts?.data?.data ? authorised_accounts.data?.data : { error: { status: authorised_accounts.error?.status, data: authorised_accounts.error?.data?.messages[0] } }
                    }
                }
            }
        }),
        logout: builder.mutation<null, void>({
            async queryFn(_arg, _queryApi, _extraOptions, baseQuery) {
                const logout: any = await baseQuery({
                    url: 'client/logout',
                    method: 'POST'
                })

                if (logout.error) return { error: logout.error }

                globalCookies.remove('XSRF-TOKEN')
                globalCookies.remove('mercury_session')

                _queryApi.dispatch(appAPI.util.resetApiState())
                _queryApi.dispatch(accountAPI.util.resetApiState())
                _queryApi.dispatch(domainAPI.util.resetApiState())
                _queryApi.dispatch(supportAPI.util.resetApiState())
                _queryApi.dispatch(shopAPI.util.resetApiState())
                _queryApi.dispatch(billingAPI.util.resetApiState())

                return {
                    data: logout.data.data
                }
            },
            invalidatesTags: ['InitialiseApplication']
        }),
        getAuthorisedAccounts: builder.query<any, any>({
            async queryFn(_arg, _queryApi, _extraOptions, baseQuery) {
                const accountsList: any = await baseQuery('client/selected-account')
                if (accountsList.error) return { error: accountsList.error }

                if (accountsList?.data?.data.length === 1) {
                    const default_account: CurrentAccount = accountsList.data.data[0]

                    /** ************************************ SELECT AUTHORISED ACCOUNT **************************************/
                    await baseQuery({
                        url: `client/selected-account/${default_account.account.id}`,
                        method: 'POST',
                        body: default_account.account.id
                    })

                    /** ************************************ FETCH USER DATA **************************************/
                    const user: any = await baseQuery('client/me')
                    if (user.error) return { error: user.error }

                    return {
                        data: {
                            session: {
                                authentication: true,
                                two_factor_required: false,
                                account: {
                                    ...default_account.account,
                                    is_primary: default_account.is_primary,
                                    expiry: default_account.expiry,
                                    role: default_account.role
                                },
                                user: user.data.data
                            }
                        }
                    }
                }

                return {
                    data: accountsList.data.data
                }
            }
        }),
        selectUser: builder.mutation<any, number | null>({
            async queryFn(_arg, _queryApi, _extraOptions, baseQuery) {
                const selected_user: any = await baseQuery({
                    url: `client/selected-account/${_arg}`,
                    method: 'POST'
                })
                if (selected_user.error) return { error: selected_user.error }

                const sessionRequest: any = await baseQuery('client/session')
                if (sessionRequest.error) return { error: sessionRequest.error }

                const { authenticated, two_factor_required, current_account, user } = sessionRequest.data.data
                const session: Session = {
                    authenticated,
                    two_factor_required,
                    account: {
                        ...current_account.account,
                        is_primary: current_account.is_primary,
                        expiry: current_account.expiry,
                        role: current_account.role
                    },
                    user
                }

                return {
                    data: {
                        session
                    }
                }
            }
        }),
        register: builder.mutation<void, SignUpFields>({
            query: (credentials) => ({
                url: 'client/register',
                method: 'POST',
                body: credentials
            })
        }),
        securityQuestions: builder.query<SecurityQuestionList, void>({
            query: () => 'client/questions',
            transformResponse: (baseQueryReturnValue: { data: SecurityQuestionList }) => baseQueryReturnValue.data
        }),
        selectMethod: builder.mutation<any, { methods: TwoFactorMethodList, type: string } | any>({
            async queryFn(_arg, _queryApi, _extraOptions, baseQuery) {
                const method: any = await baseQuery(`client/auth/two-factor?type=${_arg}`)

                return method.data?.data
                    ? {
                        data: {
                            methods: [...method.data?.data],
                            type: _arg
                        }
                    }
                    : { error: { status: method.error?.status, data: method.error?.data?.messages[0] } }
            }
        }),
        requestTwoFactorCode: builder.query<TwoFactorMethodList, any>({
            query: () => 'client/auth/two-factor',
            transformResponse: (baseQueryReturnValue: { data: TwoFactorMethodList }) => baseQueryReturnValue.data
        }),
        verifyTwoFactor: builder.mutation<any, { code: string | null, type: string }>({
            async queryFn(_arg, _queryApi, _extraOptions, baseQuery) {
                const verifyTwoFactor: any = await baseQuery({
                    url: `client/auth/two-factor?type=${_arg.type}`,
                    method: 'POST',
                    body: {
                        code: _arg.code
                    }
                })
                if (verifyTwoFactor.error) return { error: verifyTwoFactor.error }

                const authorised_accounts: any = await baseQuery('client/selected-account')

                /** ************************************ SINGLE ACCOUNT LOGIN **************************************/
                if (authorised_accounts?.data?.data.length === 1) {
                    const default_account: CurrentAccount = authorised_accounts.data.data[0]

                    /** ************************************ SELECT AUTHORISED ACCOUNT **************************************/
                    await baseQuery({
                        url: `client/selected-account/${default_account.account.id}`,
                        method: 'POST',
                        body: default_account.account.id
                    })

                    /** ************************************ FETCH USER DATA **************************************/
                    const user: any = await baseQuery('client/me')
                    if (user.error) return { error: user.error }

                    return {
                        data: {
                            session: {
                                authentication: true,
                                two_factor_required: false,
                                account: {
                                    ...default_account.account,
                                    is_primary: default_account.is_primary,
                                    expiry: default_account.expiry,
                                    role: default_account.role
                                },
                                user: user.data.data
                            }
                        }
                    }
                }

                return {
                    data: verifyTwoFactor.data.data
                }
            }
        }),
        recoverPassword: builder.mutation<void, { email: string }>({
            query: (credentials) => ({
                url: 'client/forgot-password',
                method: 'POST',
                body: credentials
            })
        }),
        resetPassword: builder.mutation<any, ResetPassword>({
            query: (credentials) => ({
                url: 'client/reset-password',
                method: 'POST',
                body: credentials
            })
        }),
        acceptInvitation: builder.query<any, any>({
            query: (token) => `client/invitation/accept/${token}`
        })
    })
})

export const {
    useGetTokenQuery,
    useInitialiseApplicationQuery,
    useLoginMutation,
    useLogoutMutation,
    useRegisterMutation,
    useSecurityQuestionsQuery,
    useGetAuthorisedAccountsQuery,
    useSelectMethodMutation,
    useSelectUserMutation,
    useRequestTwoFactorCodeQuery,
    useVerifyTwoFactorMutation,
    useRecoverPasswordMutation,
    useResetPasswordMutation,
    useAcceptInvitationQuery
} = appAPI
